/**
 * Image Cache Service
 * Provides centralized image caching to prevent images from disappearing during navigation
 */

class ImageCacheService {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.failedUrls = new Map();
    this.preloadCanvas = null;
    this.maxCacheSize = 500; // Maximum number of cached images
    this.accessOrder = new Map(); // Track access order for LRU eviction
    
    // Initialize canvas for preloading
    this.initPreloadCanvas();
  }

  initPreloadCanvas() {
    if (typeof document !== 'undefined') {
      this.preloadCanvas = document.createElement('canvas');
      this.preloadCanvas.width = 1;
      this.preloadCanvas.height = 1;
    }
  }

  normalizeIpfsPath(value) {
    if (!value || typeof value !== 'string') return null;
    const normalized = value
      .replace(/^ipfs:\/\//, '')
      .replace(/^ipfs\//, '')
      .replace(/^\/+/, '')
      .trim();

    const [cid, ...rest] = normalized.split('/');
    const cleanedRest = rest.map((segment) => segment.trim()).filter(Boolean);
    return [cid, ...cleanedRest].join('/');
  }

  sanitizeImageUrl(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return null;
    return imageUrl
      .trim()
      .replace(/\s+/g, '%20')
      .replace(/#/g, '%23');
  }

  isProblematicHost(hostname = '') {
    return hostname === 'dweb.link' || hostname.endsWith('.ipfs.dweb.link');
  }

  extractIpfsPath(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') return null;

    if (imageUrl.startsWith('ipfs://')) {
      return this.normalizeIpfsPath(imageUrl);
    }

    try {
      const parsed = new URL(imageUrl);

      if (parsed.hostname === 'cdn.bithomp.com' && parsed.pathname.startsWith('/image/')) {
        const encodedPath = parsed.pathname.replace('/image/', '');
        const decodedPath = decodeURIComponent(encodedPath);
        return this.normalizeIpfsPath(decodedPath);
      }

      if (parsed.pathname.includes('/ipfs/')) {
        const [, ipfsPath = ''] = parsed.pathname.split('/ipfs/');
        return this.normalizeIpfsPath(ipfsPath);
      }
    } catch (error) {
      // URL parse can fail for malformed URIs; continue with regex fallback
    }

    const ipfsMatch = imageUrl.match(/\/ipfs\/(.+)/);
    return ipfsMatch ? this.normalizeIpfsPath(ipfsMatch[1]) : null;
  }

  getImageCandidates(imageUrl) {
    const candidates = [];
    const pushCandidate = (candidate) => {
      if (candidate && !candidates.includes(candidate)) {
        candidates.push(candidate);
      }
    };

    const sanitizedOriginal = this.sanitizeImageUrl(imageUrl);
    let parsedOriginal = null;
    try {
      parsedOriginal = new URL(sanitizedOriginal);
    } catch (error) {
      parsedOriginal = null;
    }

    const ipfsPath = this.extractIpfsPath(sanitizedOriginal);
    if (ipfsPath) {
      // Keep gateway fanout intentionally small to avoid noisy repeated failures.
      pushCandidate(`https://nftstorage.link/ipfs/${ipfsPath}`);
      pushCandidate(`https://gateway.pinata.cloud/ipfs/${ipfsPath}`);
      if (!parsedOriginal || !this.isProblematicHost(parsedOriginal.hostname)) {
        pushCandidate(sanitizedOriginal);
      }
    } else {
      pushCandidate(sanitizedOriginal);
    }

    return candidates;
  }

  /**
   * Get cached image URL or initiate loading
   * @param {string} imageUrl - Original image URL
   * @param {string} fallbackUrl - Fallback image URL
   * @returns {Promise<string>} - Cached blob URL or original URL
   */
  async getCachedImage(imageUrl, fallbackUrl = null) {
    if (!imageUrl || imageUrl.trim() === '' || imageUrl === 'undefined' || imageUrl === 'null') {
      //console.log(`🚫 Invalid image URL: ${imageUrl}, using fallback: ${fallbackUrl}`);
      return fallbackUrl;
    }

    const normalizedImageUrl = this.sanitizeImageUrl(imageUrl);

    // For local images (like import paths), return them directly without caching
    if (normalizedImageUrl.startsWith('/') || normalizedImageUrl.startsWith('data:') || normalizedImageUrl.includes('static/media/')) {
      // //console.log(`📁 Local image detected, using directly: ${imageUrl}`);
      return normalizedImageUrl;
    }

    // If the image URL is the same as fallback, return it directly
    if (normalizedImageUrl === fallbackUrl) {
      // //console.log(`🔄 Image URL same as fallback, using directly: ${imageUrl}`);
      return normalizedImageUrl;
    }

    const failedAt = this.failedUrls.get(normalizedImageUrl);
    if (failedAt && Date.now() - failedAt < 5 * 60 * 1000) {
      return fallbackUrl || normalizedImageUrl;
    }

    // Check if already cached
    if (this.cache.has(normalizedImageUrl)) {
      this.updateAccessOrder(normalizedImageUrl);
      const cached = this.cache.get(normalizedImageUrl);
      // //console.log(`✅ Image cache hit: ${imageUrl}`);
      return cached;
    }

    // Check if already loading
    if (this.loadingPromises.has(normalizedImageUrl)) {
      // //console.log(`⏳ Image already loading: ${imageUrl}`);
      return this.loadingPromises.get(normalizedImageUrl);
    }

    // Start loading and caching
    // //console.log(`🔄 Starting to cache image: ${imageUrl}`);
    const loadingPromise = this.loadAndCacheImage(normalizedImageUrl, fallbackUrl);
    this.loadingPromises.set(normalizedImageUrl, loadingPromise);

    try {
      const cachedUrl = await loadingPromise;
      this.loadingPromises.delete(normalizedImageUrl);
      return cachedUrl;
    } catch (error) {
      this.loadingPromises.delete(normalizedImageUrl);
      //console.warn(`❌ Failed to cache image ${imageUrl}:`, error);
      this.failedUrls.set(normalizedImageUrl, Date.now());
      return fallbackUrl || null;
    }
  }

  /**
   * Load image and create cached blob URL
   * @param {string} imageUrl - Original image URL
   * @param {string} fallbackUrl - Fallback image URL
   * @returns {Promise<string>} - Cached blob URL
   */
  async loadAndCacheImage(imageUrl, fallbackUrl) {
    try {
      const candidates = this.getImageCandidates(imageUrl);
      let lastError = null;

      for (const candidate of candidates) {
        try {
          // Fetch the image with CORS mode for cacheable images
          const response = await fetch(candidate, {
            mode: 'cors',
            cache: 'force-cache'
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          // Cache against the original key so future lookups hit immediately
          this.setCachedImage(imageUrl, blobUrl);
          return blobUrl;
        } catch (candidateError) {
          lastError = candidateError;
        }
      }

      throw lastError || new Error('No image candidate could be fetched');
    } catch (error) {
      //console.warn(`Failed to load image ${imageUrl}, using fallback:`, error);
      this.failedUrls.set(imageUrl, Date.now());
      
      // If we have a fallback, try to cache that instead
      if (fallbackUrl && fallbackUrl !== imageUrl) {
        try {
          return await this.loadAndCacheImage(fallbackUrl, null);
        } catch (fallbackError) {
          //console.warn(`Fallback image also failed:`, fallbackError);
        }
      }
      
      // Avoid returning blocked cross-origin URLs as image src
      return fallbackUrl || imageUrl || null;
    }
  }

  /**
   * Set cached image with LRU eviction
   * @param {string} imageUrl - Original image URL
   * @param {string} cachedUrl - Cached blob URL
   */
  setCachedImage(imageUrl, cachedUrl) {
    // Check cache size and evict if necessary
    if (this.cache.size >= this.maxCacheSize) {
      this.evictLeastRecentlyUsed();
    }

    this.cache.set(imageUrl, cachedUrl);
    this.updateAccessOrder(imageUrl);
  }

  /**
   * Update access order for LRU tracking
   * @param {string} imageUrl - Image URL
   */
  updateAccessOrder(imageUrl) {
    this.accessOrder.delete(imageUrl);
    this.accessOrder.set(imageUrl, Date.now());
  }

  /**
   * Evict least recently used images
   */
  evictLeastRecentlyUsed() {
    // Find the oldest accessed image
    let oldestUrl = null;
    let oldestTime = Infinity;

    for (const [url, time] of this.accessOrder) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestUrl = url;
      }
    }

    if (oldestUrl) {
      const cachedUrl = this.cache.get(oldestUrl);
      if (cachedUrl && cachedUrl.startsWith('blob:')) {
        URL.revokeObjectURL(cachedUrl);
      }
      this.cache.delete(oldestUrl);
      this.accessOrder.delete(oldestUrl);
    }
  }

  /**
   * Preload images in background
   * @param {string[]} imageUrls - Array of image URLs to preload
   */
  async preloadImages(imageUrls) {
    const validUrls = imageUrls.filter(url => 
      url && 
      url.trim() !== '' && 
      url !== 'undefined' && 
      url !== 'null' &&
      !this.cache.has(url) && 
      !this.loadingPromises.has(url)
    );
    
    const preloadPromises = validUrls
      .slice(0, 10) // Limit concurrent preloads
      .map(async (url) => {
        try {
          //console.log(`🖼️ Preloading image: ${url}`);
          const result = await this.getCachedImage(url);
          //console.log(`✅ Preloaded successfully: ${url}`);
          return result;
        } catch (error) {
          //console.warn(`❌ Failed to preload: ${url}`, error);
          return null;
        }
      });

    if (preloadPromises.length > 0) {
      //console.log(`Starting preload of ${preloadPromises.length} images from ${imageUrls.length} total`);
      const results = await Promise.allSettled(preloadPromises);
      const successful = results.filter(r => r.status === 'fulfilled').length;
      //console.log(`📊 Preload complete: ${successful}/${preloadPromises.length} successful`);
    } else {
      //console.log(`ℹ️ No new images to preload from ${imageUrls.length} provided URLs (most are CDN images)`);
    }
  }

  /**
   * Clear all cached images
   */
  clearCache() {
    // Revoke all blob URLs to free memory
    for (const cachedUrl of this.cache.values()) {
      if (cachedUrl && cachedUrl.startsWith('blob:')) {
        URL.revokeObjectURL(cachedUrl);
      }
    }
    
    this.cache.clear();
    this.accessOrder.clear();
    this.loadingPromises.clear();
    this.failedUrls.clear();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      loadingCount: this.loadingPromises.size,
      usage: (this.cache.size / this.maxCacheSize * 100).toFixed(1) + '%'
    };
  }

  /**
   * Check if image is cached
   * @param {string} imageUrl - Image URL to check
   * @returns {boolean} True if cached
   */
  isCached(imageUrl) {
    return this.cache.has(imageUrl);
  }

  /**
   * Remove specific image from cache
   * @param {string} imageUrl - Image URL to remove
   */
  removeFromCache(imageUrl) {
    const cachedUrl = this.cache.get(imageUrl);
    if (cachedUrl && cachedUrl.startsWith('blob:')) {
      URL.revokeObjectURL(cachedUrl);
    }
    this.cache.delete(imageUrl);
    this.accessOrder.delete(imageUrl);
  }
}

// Create singleton instance
const imageCache = new ImageCacheService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    imageCache.clearCache();
  });
}

export default imageCache;
